import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, FileText, X, Send, Eye, CheckCircle,
  Euro, Calendar, Clock, Download, Mail, Pencil, Copy, ExternalLink, PenTool,
  Package, Wrench, Percent, Inbox, AlertCircle, Phone, MapPin, MessageCircle,
  ThumbsUp, ThumbsDown, ArrowRight, User
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Statuts des demandes de devis reçues
const requestStatusOptions = [
  { value: 'pending', label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  { value: 'accepted', label: 'Acceptée', color: 'bg-green-100 text-green-700', icon: ThumbsUp },
  { value: 'refused', label: 'Refusée', color: 'bg-red-100 text-red-700', icon: ThumbsDown },
  { value: 'converted', label: 'Convertie', color: 'bg-blue-100 text-blue-700', icon: FileText },
];

const statusOptions = [
  { value: 'draft', label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  { value: 'sent', label: 'Envoyé', color: 'bg-blue-100 text-blue-600' },
  { value: 'accepted', label: 'Accepté', color: 'bg-green-100 text-green-600' },
  { value: 'refused', label: 'Refusé', color: 'bg-red-100 text-red-600' },
  { value: 'expired', label: 'Expiré', color: 'bg-amber-100 text-amber-600' },
];

const chantierTypes = [
  { value: 'renovation', label: 'Rénovation' },
  { value: 'construction', label: 'Construction' },
  { value: 'extension', label: 'Extension' },
  { value: 'plomberie', label: 'Plomberie' },
  { value: 'electricite', label: 'Électricité' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'autre', label: 'Autre' },
];

export default function DevisPage() {
  const { user } = useOutletContext();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'opportunities' ? 'opportunities' : 'opportunities';
  const [activeTab, setActiveTab] = useState(initialTab); // 'opportunities', 'requests' ou 'quotes'
  const [quotes, setQuotes] = useState([]);
  const [devisRequests, setDevisRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRequestDetailModal, setShowRequestDetailModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [publicRequests, setPublicRequests] = useState([]);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseForm, setResponseForm] = useState({ price: '', message: '', validity_days: 30 });
  
  const [newQuote, setNewQuote] = useState({
    client_id: '',
    chantier_type: 'autre',
    chantier_address: '',
    work_description: '',
    items: [{ description: '', quantity: 1, unit: 'u', unit_price: 0, discount: 0 }],
    tax_rate: 20,
    validity_days: 30,
    estimated_duration_days: 0,
    notes: ''
  });

  // Autocomplete state
  const [catalogResults, setCatalogResults] = useState([]);
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const autocompleteRef = useRef(null);

  // Données DÉMO pour les demandes de devis reçues
  const DEMO_DEVIS_REQUESTS = [
    {
      id: 'req-1',
      prestation_id: 'presta-1',
      prestation_name: 'Pose de carrelage sol',
      artisan_id: 'artisan-3',
      artisan_shop_name: 'Mon entreprise',
      quantity: 25,
      quantity_unit: 'm²',
      estimated_price: 1125,
      description: 'Je souhaite refaire le carrelage de ma salle de bain (15m²) et de mon entrée (10m²). Carrelage actuel à retirer.',
      address: '12 rue des Lilas',
      city: 'Toulouse',
      postal_code: '31000',
      preferred_date: '2026-02-15',
      phone: '06 12 34 56 78',
      client_name: 'Marie Dupont',
      client_email: 'marie.dupont@email.com',
      status: 'pending',
      created_at: '2026-01-27T10:30:00Z',
    },
    {
      id: 'req-2',
      prestation_id: 'presta-2',
      prestation_name: 'Réparation fuite d\'eau',
      artisan_id: 'artisan-3',
      artisan_shop_name: 'Mon entreprise',
      quantity: 1,
      quantity_unit: 'intervention',
      estimated_price: 80,
      description: 'Fuite sous l\'évier de la cuisine. L\'eau coule depuis 2 jours. Urgent.',
      address: '45 avenue Jean Jaurès',
      city: 'Toulouse',
      postal_code: '31400',
      preferred_date: null,
      phone: '06 98 76 54 32',
      client_name: 'Pierre Martin',
      client_email: 'p.martin@gmail.com',
      status: 'pending',
      created_at: '2026-01-27T14:15:00Z',
    },
    {
      id: 'req-3',
      prestation_id: 'presta-3',
      prestation_name: 'Peinture intérieure',
      artisan_id: 'artisan-3',
      artisan_shop_name: 'Mon entreprise',
      quantity: 45,
      quantity_unit: 'm²',
      estimated_price: 1125,
      description: 'Peinture du salon et de la salle à manger. Murs en bon état, juste rafraîchissement.',
      address: '8 place du Capitole',
      city: 'Toulouse',
      postal_code: '31000',
      preferred_date: '2026-03-01',
      phone: '07 11 22 33 44',
      client_name: 'Sophie Bernard',
      client_email: 'sophie.b@outlook.fr',
      status: 'accepted',
      created_at: '2026-01-25T09:00:00Z',
    },
  ];

  // Données DÉMO pour le mode sans authentification (VIDE pour nouvel utilisateur)
  const DEMO_QUOTES = [];

  const DEMO_CLIENTS = [];

  useEffect(() => {
    fetchQuotes();
    fetchClients();
    fetchDevisRequests();
    fetchPublicRequests();
    if (searchParams.get('action') === 'new') {
      setShowCreateModal(true);
    }
  }, [searchParams]);

  // Récupérer les demandes de devis PUBLIQUES (opportunités)
  const fetchPublicRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/artisans/public-devis-requests?status=pending`);
      if (response.ok) {
        const data = await response.json();
        setPublicRequests(data.devis_requests || []);
      }
    } catch (error) {
      console.error('Error fetching public requests:', error);
    }
  };

  // Récupérer les demandes de devis reçues
  const fetchDevisRequests = async () => {
    const token = localStorage.getItem('client_token');
    
    if (!token) {
      setDevisRequests(DEMO_DEVIS_REQUESTS);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/artisans/devis-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDevisRequests(data.devis_requests || []);
      } else {
        setDevisRequests(DEMO_DEVIS_REQUESTS);
      }
    } catch (error) {
      console.error('Error fetching devis requests:', error);
      setDevisRequests(DEMO_DEVIS_REQUESTS);
    }
  };

  // Mettre à jour le statut d'une demande
  const handleUpdateRequestStatus = async (requestId, newStatus) => {
    const token = localStorage.getItem('client_token');
    
    try {
      if (token) {
        const response = await fetch(`${API_URL}/api/artisans/devis-requests/${requestId}?status=${newStatus}`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Erreur serveur');
      }
      
      // Mettre à jour localement
      setDevisRequests(prev => prev.map(req => 
        req.id === requestId ? { ...req, status: newStatus } : req
      ));
      
      const statusLabel = requestStatusOptions.find(s => s.value === newStatus)?.label;
      toast.success(`Demande ${statusLabel?.toLowerCase()}`);
      setShowRequestDetailModal(false);
      
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Convertir une demande en devis
  const handleConvertToQuote = (request) => {
    // Pré-remplir le formulaire de devis avec les infos de la demande
    setNewQuote({
      ...newQuote,
      chantier_address: `${request.address}, ${request.postal_code} ${request.city}`,
      work_description: request.description,
      items: [{
        description: request.prestation_name,
        quantity: request.quantity,
        unit: request.quantity_unit,
        unit_price: request.estimated_price / request.quantity,
        discount: 0
      }],
    });
    
    // Marquer la demande comme convertie
    handleUpdateRequestStatus(request.id, 'converted');
    
    // Ouvrir le modal de création de devis
    setShowRequestDetailModal(false);
    setShowCreateModal(true);
  };

  // Répondre à une demande publique avec un prix
  const handleRespondToRequest = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      toast.error('Vous devez être connecté pour répondre');
      return;
    }

    if (!responseForm.price || !responseForm.message) {
      toast.error('Veuillez remplir le prix et le message');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/artisans/devis-requests/${respondingTo.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          price: parseFloat(responseForm.price),
          message: responseForm.message,
          validity_days: responseForm.validity_days
        })
      });

      if (response.ok) {
        toast.success('Votre proposition a été envoyée au client !');
        setShowRespondModal(false);
        setRespondingTo(null);
        setResponseForm({ price: '', message: '', validity_days: 30 });
        // Rafraîchir les listes
        fetchPublicRequests();
        fetchDevisRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error responding to request:', error);
      toast.error('Erreur de connexion');
    }
  };

  const openRespondModal = (request) => {
    setRespondingTo(request);
    setResponseForm({ 
      price: request.estimated_price ? String(request.estimated_price) : '', 
      message: '', 
      validity_days: 30 
    });
    setShowRespondModal(true);
  };

  const fetchQuotes = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO : Si pas de token, utiliser les données fictives
    if (!token) {
      setQuotes(DEMO_QUOTES);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/batiment/quotes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setQuotes(await response.json());
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      // En cas d'erreur, utiliser les données démo
      setQuotes(DEMO_QUOTES);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO
    if (!token) {
      setClients(DEMO_CLIENTS);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/batiment/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setClients(await response.json());
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients(DEMO_CLIENTS);
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    if (!newQuote.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/batiment/quotes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newQuote)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Devis ${data.quote_number} créé`);
        setShowCreateModal(false);
        resetForm();
        fetchQuotes();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateStatus = async (quoteId, newStatus) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/batiment/quotes/${quoteId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchQuotes();
        if (selectedQuote?.id === quoteId) {
          setSelectedQuote(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/batiment/quotes/${quoteId}/convert-to-invoice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Facture ${data.invoice_number} créée`);
        setShowDetailModal(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  const addItem = () => {
    setNewQuote(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit: 'u', unit_price: 0, discount: 0 }]
    }));
  };

  const removeItem = (index) => {
    setNewQuote(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setNewQuote(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotals = () => {
    const subtotal = newQuote.items.reduce((sum, item) => {
      const lineTotal = item.quantity * item.unit_price;
      const discountAmount = lineTotal * ((item.discount || 0) / 100);
      return sum + (lineTotal - discountAmount);
    }, 0);
    const tax = subtotal * (newQuote.tax_rate / 100);
    return { subtotal, tax, total: subtotal + tax };
  };

  // Search catalog (products + services) for autocomplete
  const searchCatalog = async (query, index) => {
    if (query.length < 2) {
      setCatalogResults([]);
      setShowAutocomplete(false);
      return;
    }

    const token = localStorage.getItem('client_token');
    try {
      const [productsRes, servicesRes] = await Promise.all([
        fetch(`${API_URL}/api/batiment/products/search?q=${encodeURIComponent(query)}&limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/batiment/services/search?q=${encodeURIComponent(query)}&limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const products = productsRes.ok ? await productsRes.json() : [];
      const services = servicesRes.ok ? await servicesRes.json() : [];

      const combined = [
        ...products.map(p => ({ ...p, type: 'product' })),
        ...services.map(s => ({ ...s, type: 'service' }))
      ];

      setCatalogResults(combined);
      setActiveItemIndex(index);
      setShowAutocomplete(combined.length > 0);
    } catch (error) {
      console.error('Error searching catalog:', error);
    }
  };

  // Select item from autocomplete
  const selectCatalogItem = (item, index) => {
    setNewQuote(prev => ({
      ...prev,
      items: prev.items.map((it, i) => 
        i === index ? {
          ...it,
          description: item.name + (item.description ? ` - ${item.description}` : ''),
          unit: item.unit || 'u',
          unit_price: item.unit_price || 0,
          discount: 0
        } : it
      )
    }));
    setCatalogResults([]);
    setShowAutocomplete(false);
    setActiveItemIndex(null);
  };

  // Handle click outside autocomplete
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetForm = () => {
    setNewQuote({
      client_id: '',
      chantier_type: 'autre',
      chantier_address: '',
      work_description: '',
      items: [{ description: '', quantity: 1, unit: 'u', unit_price: 0, discount: 0 }],
      tax_rate: 20,
      validity_days: 30,
      estimated_duration_days: 0,
      notes: ''
    });
    setCatalogResults([]);
    setShowAutocomplete(false);
  };

  const openDetailModal = async (quote) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/batiment/quotes/${quote.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSelectedQuote(await response.json());
        setShowDetailModal(true);
      }
    } catch (error) {
      setSelectedQuote(quote);
      setShowDetailModal(true);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];

  const filteredQuotes = quotes.filter(q => {
    const matchesSearch = q.quote_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         q.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !filterStatus || q.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="devis-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Devis</h1>
          <p className="text-rm-text-secondary">Gérez vos demandes et devis</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-rm-primary hover:bg-rm-primary/90" data-testid="new-quote-btn">
          <Plus className="w-5 h-5 mr-2" />
          Nouveau devis
        </Button>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab('opportunities')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'opportunities'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Search size={18} />
          Opportunités
          {publicRequests.length > 0 && (
            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
              {publicRequests.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'requests'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Inbox size={18} />
          Demandes reçues
          {devisRequests.filter(r => r.status === 'pending').length > 0 && (
            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
              {devisRequests.filter(r => r.status === 'pending').length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('quotes')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'quotes'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <FileText size={18} />
          Mes devis
          <span className="bg-slate-200 text-slate-600 text-xs px-2 py-0.5 rounded-full">
            {quotes.length}
          </span>
        </button>
      </div>

      {/* Contenu selon l'onglet actif */}
      {activeTab === 'opportunities' ? (
        <>
          {/* Liste des opportunités (demandes publiques) */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                  <Search className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Demandes de clients à proximité</h3>
                  <p className="text-sm text-slate-600">Ces clients cherchent un artisan comme vous. Proposez votre tarif !</p>
                </div>
              </div>
            </div>
            {publicRequests.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-rm-text font-medium">Aucune opportunité disponible</p>
                <p className="text-rm-text-secondary text-sm mt-1">Les nouvelles demandes de clients apparaîtront ici</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {publicRequests.map((request) => (
                  <div key={request.id} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* Icône type de projet */}
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold">
                        {request.prestation_name?.charAt(0) || '?'}
                      </div>
                      
                      {/* Contenu */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-rm-text">{request.prestation_name}</h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                            Nouvelle
                          </span>
                          {request.urgency === 'urgent' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                              Urgent
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-rm-text-secondary mt-1">
                          <span className="font-medium">{request.client_name}</span> • {request.city} ({request.postal_code})
                        </p>
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{request.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>Budget: {request.budget_range === 'small' ? '< 1k€' : 
                                        request.budget_range === 'medium' ? '1k-5k€' :
                                        request.budget_range === 'large' ? '5k-15k€' : 
                                        request.budget_range === 'xlarge' ? '15k-50k€' : '> 50k€'}</span>
                          <span>•</span>
                          <span>Délai: {request.urgency === 'urgent' ? '48h' : 
                                       request.urgency === 'rapide' ? '1 semaine' : 
                                       request.urgency === 'normal' ? '1 mois' : 'Flexible'}</span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRespondModal(request);
                          }}
                        >
                          <Send size={14} className="mr-1" />
                          Proposer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRequest(request);
                            setShowRequestDetailModal(true);
                          }}
                        >
                          <Eye size={14} className="mr-1" />
                          Détails
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : activeTab === 'requests' ? (
        <>
          {/* Liste des demandes de devis reçues */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100">
            {devisRequests.length === 0 ? (
              <div className="text-center py-12">
                <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-rm-text font-medium">Aucune demande de devis</p>
                <p className="text-rm-text-secondary text-sm mt-1">Les demandes de vos clients apparaîtront ici</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {devisRequests.map((request) => {
                  const statusInfo = requestStatusOptions.find(s => s.value === request.status);
                  const StatusIcon = statusInfo?.icon || Clock;
                  return (
                    <div
                      key={request.id}
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowRequestDetailModal(true);
                      }}
                      className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start gap-4">
                        {/* Icône */}
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          request.status === 'pending' ? 'bg-amber-100' : 
                          request.status === 'accepted' ? 'bg-green-100' : 
                          request.status === 'refused' ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          <StatusIcon className={`w-6 h-6 ${
                            request.status === 'pending' ? 'text-amber-600' : 
                            request.status === 'accepted' ? 'text-green-600' : 
                            request.status === 'refused' ? 'text-red-600' : 'text-blue-600'
                          }`} />
                        </div>
                        
                        {/* Contenu */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-rm-text">{request.prestation_name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${statusInfo?.color}`}>
                              {statusInfo?.label}
                            </span>
                            {request.status === 'pending' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                                Nouveau
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-rm-text-secondary mt-1">
                            <span className="font-medium">{request.client_name}</span> • {request.city}
                          </p>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-1">{request.description}</p>
                        </div>
                        
                        {/* Prix et date */}
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-rm-text">
                            {request.estimated_price > 0 ? `≈ ${request.estimated_price.toLocaleString()}€` : 'Sur devis'}
                          </p>
                          <p className="text-xs text-rm-text-secondary">
                            {request.quantity} {request.quantity_unit}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(request.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Filters pour les devis */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un devis..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
            >
              <option value="">Tous les statuts</option>
              {statusOptions.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Quotes List */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100">
            {filteredQuotes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-rm-text-secondary">Aucun devis trouvé</p>
                <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-rm-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un devis
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredQuotes.map((quote) => {
                  const statusInfo = getStatusInfo(quote.status);
                  return (
                    <div
                      key={quote.id}
                      onClick={() => openDetailModal(quote)}
                      className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                      data-testid={`quote-${quote.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-rm-text">{quote.quote_number}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                          </div>
                          <p className="text-sm text-rm-text-secondary">{quote.client_name}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-rm-text">{formatCurrency(quote.total)}</p>
                          <p className="text-xs text-rm-text-secondary">{formatDate(quote.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-3xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-rm-text">Nouveau devis</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateQuote} className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Client Selection */}
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Client *</label>
                  {clients.length === 0 ? (
                    <div className="p-4 bg-amber-50 rounded-lg text-amber-700 text-sm">
                      Aucun client disponible. <Link to="/espace-client/crm-batiment/clients" className="underline">Crée d'abord un client</Link>
                    </div>
                  ) : (
                    <select
                      required
                      value={newQuote.client_id}
                      onChange={(e) => setNewQuote({ ...newQuote, client_id: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      <option value="">Sélectionner un client</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.name} {c.company_name ? `(${c.company_name})` : ''}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Type de chantier</label>
                    <select
                      value={newQuote.chantier_type}
                      onChange={(e) => setNewQuote({ ...newQuote, chantier_type: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      {chantierTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Validité (jours)</label>
                    <input
                      type="number"
                      value={newQuote.validity_days}
                      onChange={(e) => setNewQuote({ ...newQuote, validity_days: parseInt(e.target.value) || 30 })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Adresse du chantier</label>
                  <input
                    type="text"
                    value={newQuote.chantier_address}
                    onChange={(e) => setNewQuote({ ...newQuote, chantier_address: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="Adresse du chantier (si différente du client)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Description des travaux *</label>
                  <textarea
                    required
                    value={newQuote.work_description}
                    onChange={(e) => setNewQuote({ ...newQuote, work_description: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    rows={3}
                    placeholder="Description détaillée des travaux à effectuer..."
                  />
                </div>

                {/* Items */}
                <div ref={autocompleteRef}>
                  <label className="block text-sm font-medium text-rm-text mb-2">Lignes du devis</label>
                  <p className="text-xs text-rm-text-secondary mb-3">
                    Tapez pour rechercher dans ton catalogue de produits et prestations
                  </p>
                  <div className="space-y-3">
                    {newQuote.items.map((item, index) => (
                      <div key={index} className="relative">
                        <div className="grid grid-cols-12 gap-2 items-start">
                          <div className="col-span-4 relative">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => {
                                updateItem(index, 'description', e.target.value);
                                searchCatalog(e.target.value, index);
                              }}
                              onFocus={() => {
                                if (item.description.length >= 2) {
                                  searchCatalog(item.description, index);
                                }
                              }}
                              placeholder="Description (tapez pour rechercher)"
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
                            />
                            {/* Autocomplete dropdown */}
                            {showAutocomplete && activeItemIndex === index && catalogResults.length > 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {catalogResults.map((catalogItem, i) => (
                                  <button
                                    key={`${catalogItem.type}-${catalogItem.id}-${i}`}
                                    type="button"
                                    onClick={() => selectCatalogItem(catalogItem, index)}
                                    className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2 text-sm border-b border-slate-100 last:border-0"
                                  >
                                    {catalogItem.type === 'product' ? (
                                      <Package className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                    ) : (
                                      <Wrench className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-rm-text truncate">{catalogItem.name}</p>
                                      <p className="text-xs text-rm-text-secondary">
                                        {catalogItem.unit_price?.toFixed(2)}€/{catalogItem.unit}
                                      </p>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="col-span-1">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                              placeholder="Qté"
                              className="w-full px-2 py-2 text-sm border border-slate-200 rounded-lg text-center"
                            />
                          </div>
                          <div className="col-span-1">
                            <select
                              value={item.unit}
                              onChange={(e) => updateItem(index, 'unit', e.target.value)}
                              className="w-full px-1 py-2 text-sm border border-slate-200 rounded-lg"
                            >
                              <option value="u">u</option>
                              <option value="m²">m²</option>
                              <option value="m">m</option>
                              <option value="h">h</option>
                              <option value="j">j</option>
                              <option value="forfait">forf.</option>
                            </select>
                          </div>
                          <div className="col-span-2">
                            <input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              placeholder="Prix unit."
                              className="w-full px-2 py-2 text-sm border border-slate-200 rounded-lg"
                            />
                          </div>
                          <div className="col-span-2">
                            <div className="relative">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={item.discount || 0}
                                onChange={(e) => updateItem(index, 'discount', parseFloat(e.target.value) || 0)}
                                placeholder="Remise"
                                className="w-full px-2 py-2 pr-6 text-sm border border-slate-200 rounded-lg"
                              />
                              <Percent className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-slate-400" />
                            </div>
                          </div>
                          <div className="col-span-1 text-right">
                            <p className="text-sm font-medium text-rm-text py-2">
                              {formatCurrency((item.quantity * item.unit_price) * (1 - (item.discount || 0) / 100))}
                            </p>
                          </div>
                          <div className="col-span-1">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              disabled={newQuote.items.length === 1}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button type="button" variant="outline" onClick={addItem} className="flex-1">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter une ligne
                    </Button>
                    <Link to="/espace-client/crm-batiment/produits" className="text-xs text-rm-primary hover:underline flex items-center">
                      <Package className="w-3 h-3 mr-1" />
                      Gérer produits
                    </Link>
                    <Link to="/espace-client/crm-batiment/prestations" className="text-xs text-rm-primary hover:underline flex items-center">
                      <Wrench className="w-3 h-3 mr-1" />
                      Gérer prestations
                    </Link>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-rm-text-secondary">Sous-total HT</span>
                    <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-rm-text-secondary">TVA</span>
                    <div className="flex items-center gap-2">
                      <select
                        value={newQuote.tax_rate}
                        onChange={(e) => setNewQuote({ ...newQuote, tax_rate: parseFloat(e.target.value) })}
                        className="px-2 py-1 border border-slate-200 rounded text-xs"
                      >
                        <option value="0">0%</option>
                        <option value="5.5">5.5%</option>
                        <option value="10">10%</option>
                        <option value="20">20%</option>
                      </select>
                      <span className="font-medium">{formatCurrency(totals.tax)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2">
                    <span>Total TTC</span>
                    <span className="text-rm-primary">{formatCurrency(totals.total)}</span>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
                  <Button type="submit" className="bg-rm-primary" disabled={clients.length === 0}>
                    Créer le devis
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedQuote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-rm-text">{selectedQuote.quote_number}</h2>
                    <p className="text-rm-text-secondary">{selectedQuote.client_name}</p>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Status */}
                <div>
                  <h4 className="font-semibold text-rm-text mb-2">Statut</h4>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map(s => (
                      <button
                        key={s.value}
                        onClick={() => handleUpdateStatus(selectedQuote.id, s.value)}
                        className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                          selectedQuote.status === s.value ? s.color : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-rm-text-secondary">Date de création</span>
                    <p className="font-medium">{formatDate(selectedQuote.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-rm-text-secondary">Valide jusqu'au</span>
                    <p className="font-medium">{formatDate(selectedQuote.valid_until)}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-rm-text-secondary">Description des travaux</span>
                    <p className="mt-1">{selectedQuote.work_description}</p>
                  </div>
                </div>

                {/* Items */}
                {selectedQuote.items?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-rm-text mb-2">Lignes du devis</h4>
                    <div className="bg-slate-50 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left p-3">Description</th>
                            <th className="text-right p-3">Qté</th>
                            <th className="text-right p-3">P.U.</th>
                            <th className="text-right p-3">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedQuote.items.map((item, i) => (
                            <tr key={i} className="border-t border-slate-200">
                              <td className="p-3">{item.description}</td>
                              <td className="p-3 text-right">{item.quantity} {item.unit}</td>
                              <td className="p-3 text-right">{formatCurrency(item.unit_price)}</td>
                              <td className="p-3 text-right font-medium">{formatCurrency(item.quantity * item.unit_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Totals */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Sous-total HT</span>
                    <span>{formatCurrency(selectedQuote.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>TVA ({selectedQuote.tax_rate}%)</span>
                    <span>{formatCurrency(selectedQuote.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2">
                    <span>Total TTC</span>
                    <span className="text-rm-primary">{formatCurrency(selectedQuote.total)}</span>
                  </div>
                </div>

                {/* Signature */}
                {selectedQuote.signed && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">Devis signé</span>
                    </div>
                    <p className="text-sm text-green-600">
                      Signé par {selectedQuote.signed_by} le {formatDate(selectedQuote.signed_at)}
                    </p>
                    {selectedQuote.signature_url && (
                      <div className="mt-3 p-2 bg-white rounded border border-green-200">
                        <img 
                          src={selectedQuote.signature_url} 
                          alt="Signature" 
                          className="max-h-20 mx-auto"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Lien de signature (si non signé) */}
                {!selectedQuote.signed && selectedQuote.status !== 'refused' && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-700 mb-2">
                      <PenTool className="w-5 h-5" />
                      <span className="font-semibold">Signature électronique</span>
                    </div>
                    <p className="text-sm text-blue-600 mb-3">
                      Partage ce lien avec ton client pour qu'il signe le devis en ligne
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const url = `${window.location.origin}/devis/${selectedQuote.id}`;
                          navigator.clipboard.writeText(url);
                          toast.success('Lien copié dans le presse-papiers');
                        }}
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copier le lien
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/devis/${selectedQuote.id}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-between">
                <div className="flex gap-2">
                  {selectedQuote.status === 'accepted' && !selectedQuote.invoice_id && (
                    <Button onClick={() => handleConvertToInvoice(selectedQuote.id)} variant="outline">
                      Convertir en facture
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setShowDetailModal(false)}>Fermer</Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Détail Demande de Devis */}
      <AnimatePresence>
        {showRequestDetailModal && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowRequestDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-orange-50 to-amber-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-rm-text">Demande de devis</h2>
                    <p className="text-sm text-slate-500">
                      Reçue le {new Date(selectedRequest.created_at).toLocaleDateString('fr-FR', { 
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                      })}
                    </p>
                  </div>
                  <button onClick={() => setShowRequestDetailModal(false)} className="p-2 hover:bg-white/50 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Prestation demandée */}
                <div className="bg-orange-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1">Prestation demandée</p>
                  <p className="text-lg font-bold text-slate-900">{selectedRequest.prestation_name}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm text-slate-600">
                      <strong>{selectedRequest.quantity}</strong> {selectedRequest.quantity_unit}
                    </span>
                    <span className="text-lg font-bold text-orange-600">
                      {selectedRequest.estimated_price > 0 ? `≈ ${selectedRequest.estimated_price.toLocaleString()}€` : 'Sur devis'}
                    </span>
                  </div>
                </div>

                {/* Client */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Client</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{selectedRequest.client_name || 'Client anonyme'}</p>
                      {selectedRequest.client_email && (
                        <p className="text-sm text-slate-500">{selectedRequest.client_email}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Contact */}
                  <div className="flex gap-2 mt-3">
                    <a 
                      href={`tel:${selectedRequest.phone}`}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Phone size={16} />
                      {selectedRequest.phone}
                    </a>
                    <button className="flex items-center justify-center gap-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg text-sm font-medium hover:bg-[#1E3A5F]/90">
                      <MessageCircle size={16} />
                      Chat
                    </button>
                  </div>
                </div>

                {/* Adresse du chantier */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Adresse du chantier</p>
                  <div className="flex items-start gap-2">
                    <MapPin size={18} className="text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{selectedRequest.address}</p>
                      <p className="text-slate-600">{selectedRequest.postal_code} {selectedRequest.city}</p>
                    </div>
                  </div>
                </div>

                {/* Date souhaitée */}
                {selectedRequest.preferred_date && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Date souhaitée</p>
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-slate-400" />
                      <p className="font-medium text-slate-900">
                        {new Date(selectedRequest.preferred_date).toLocaleDateString('fr-FR', { 
                          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {/* Description du projet */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Description du projet</p>
                  <p className="text-slate-700 whitespace-pre-line">{selectedRequest.description}</p>
                </div>

                {/* Statut actuel */}
                <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                  <span className="text-sm text-slate-600">Statut actuel :</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    requestStatusOptions.find(s => s.value === selectedRequest.status)?.color
                  }`}>
                    {requestStatusOptions.find(s => s.value === selectedRequest.status)?.label}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 border-t border-slate-100 bg-slate-50">
                {selectedRequest.status === 'pending' ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleUpdateRequestStatus(selectedRequest.id, 'refused')}
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      Refuser
                    </Button>
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleUpdateRequestStatus(selectedRequest.id, 'accepted')}
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      Accepter
                    </Button>
                  </div>
                ) : selectedRequest.status === 'accepted' ? (
                  <Button
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => handleConvertToQuote(selectedRequest)}
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Créer le devis
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowRequestDetailModal(false)}
                  >
                    Fermer
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Répondre à une demande */}
      <AnimatePresence>
        {showRespondModal && respondingTo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowRespondModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-orange-50 to-amber-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">Proposer votre tarif</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      {respondingTo.prestation_name} • {respondingTo.city}
                    </p>
                  </div>
                  <button 
                    onClick={() => setShowRespondModal(false)} 
                    className="p-2 hover:bg-white/50 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-6 space-y-4">
                {/* Résumé de la demande */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-800 mb-2">Demande du client</h4>
                  <p className="text-sm text-slate-600">{respondingTo.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span>Client: {respondingTo.client_name}</span>
                    <span>•</span>
                    <span>Budget estimé: {respondingTo.estimated_price?.toLocaleString('fr-FR')} €</span>
                  </div>
                </div>

                {/* Formulaire */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre prix (€) *
                  </label>
                  <input
                    type="number"
                    value={responseForm.price}
                    onChange={(e) => setResponseForm(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="Ex: 850"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre message au client *
                  </label>
                  <textarea
                    value={responseForm.message}
                    onChange={(e) => setResponseForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Présentez votre offre, vos disponibilités, vos points forts..."
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Validité de l'offre (jours)
                  </label>
                  <select
                    value={responseForm.validity_days}
                    onChange={(e) => setResponseForm(prev => ({ ...prev, validity_days: parseInt(e.target.value) }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value={7}>7 jours</option>
                    <option value={15}>15 jours</option>
                    <option value={30}>30 jours</option>
                    <option value={60}>60 jours</option>
                  </select>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowRespondModal(false)}>
                  Annuler
                </Button>
                <Button 
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={handleRespondToRequest}
                >
                  <Send size={16} className="mr-2" />
                  Envoyer ma proposition
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
